import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { usePlugins } from '../../context/PluginContext';
import { type Plugin } from '../../context/PluginContext';
import { APP_CONFIG } from '../../config/constants';

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

// Ícone de fechar (X)
const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

export function SearchBar() {
    const { plugins } = usePlugins();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<Plugin[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    // NOVO: Estado para controlar se a barra de pesquisa está expandida
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);

    useEffect(() => {
        if (query.trim().length > 1) {
            const filteredPlugins = plugins.filter(plugin =>
                plugin.name.toLowerCase().includes(query.toLowerCase())
            );
            setResults(filteredPlugins);
            setIsOpen(true);
        } else {
            setResults([]);
            setIsOpen(false);
        }
    }, [query, plugins]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                // NOVO: Fecha a barra de pesquisa ao clicar fora
                setIsSearchExpanded(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [searchRef]);

    const handleResultClick = () => {
        setQuery('');
        setIsOpen(false);
        setIsSearchExpanded(false); // NOVO: Fecha a barra ao selecionar um item
    }

    // NOVO: Função para alternar a barra de pesquisa
    const toggleSearch = () => {
        setIsSearchExpanded(!isSearchExpanded);
    };

    return (
        <div className="relative" ref={searchRef}>
            {/* NOVO: Barra de pesquisa embutida na navbar para telas grandes */}
            <div className="relative hidden md:block">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => query.trim().length > 1 && setIsOpen(true)}
                    placeholder="Encontre o plugin perfeito..."
                    className="bg-background border border-border-color rounded-lg py-3 pl-12 pr-4 w-full md:w-96 text-sm placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-brand-blue transition-all"
                />
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-text-secondary">
                    <SearchIcon />
                </div>
            </div>

            {/* NOVO: Ícone de pesquisa para telas pequenas */}
            <button
                onClick={toggleSearch}
                className="md:hidden p-2 rounded-full hover:bg-gray-100 text-text-secondary transition-colors"
                aria-label="Abrir pesquisa"
            >
                <SearchIcon />
            </button>

            {/* NOVO: Barra de pesquisa flutuante que aparece em telas pequenas */}
            <Transition
                show={isSearchExpanded}
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
            >
                <div className="md:hidden fixed inset-x-0 top-0 mt-16 p-4 bg-card-bg border-b border-border-color z-30 shadow-lg">
                    <div className="relative">
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onFocus={() => query.trim().length > 1 && setIsOpen(true)}
                            placeholder="Encontre o plugin perfeito..."
                            className="bg-background border border-border-color rounded-lg py-3 pl-12 pr-4 w-full text-sm placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-brand-blue transition-all"
                        />
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-text-secondary">
                            <SearchIcon />
                        </div>
                        {/* NOVO: Botão para fechar a pesquisa */}
                        <button
                            onClick={toggleSearch}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-text-secondary hover:text-text-primary"
                        >
                            <CloseIcon />
                        </button>
                    </div>
                </div>
            </Transition>


            <Transition
                show={isOpen && results.length > 0}
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <div className="absolute top-full mt-2 w-full bg-card-bg rounded-lg shadow-subtle border border-border-color z-20 max-h-96 overflow-y-auto">
                    <div className="p-2">
                        <p className="px-3 py-2 text-xs font-semibold text-text-secondary uppercase">Resultados</p>
                        <ul>
                            {results.map((plugin) => (
                                <li key={plugin.id}>
                                    <Link
                                        to={`/plugins/${plugin.slug}`}
                                        onClick={handleResultClick}
                                        className="flex items-center justify-between gap-4 p-3 hover:bg-background rounded-md transition-colors"
                                    >
                                        <div className="flex items-center gap-4">
                                            <img
                                                src={plugin.iconUrl
                                                    ? `${APP_CONFIG.uploadsBaseUrl}${plugin.iconUrl}`
                                                    : "https://static.thenounproject.com/png/966287-200.png"
                                                }
                                                alt=""
                                                className="w-10 h-10 bg-background rounded-md flex-shrink-0 object-contain p-1"
                                            />
                                            <div>
                                                <p className="font-semibold text-text-primary text-sm">{plugin.name}</p>
                                                <p className="text-xs text-text-secondary">{plugin.authorId}</p>
                                            </div>
                                        </div>
                                        <span className="text-sm font-bold text-text-primary">
                                            {plugin.price === 0 ? 'Grátis' : `R$ ${plugin.price.toFixed(2)}`}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Transition>
        </div>
    );
}